import React, { useEffect, useMemo, useRef, useState } from "react";
import "./search-box.css";
import { Icon } from "@/components/ui-components/icon";
import { formatCurrency } from "@/utils/format";
import { isPlaceholderImage } from "@/utils/assets";
import type { Product } from "@/data/products";
import type { LocaleCode } from "@/i18n/locales";
import type { InterpolationValues } from "@/i18n/types";

interface Props {
    locale: LocaleCode;
    placeholder: string;
    query: string;
    results: Product[];
    t: (key: string, values?: InterpolationValues) => string;
    onChange: (query: string) => void;
    onSelect: (product: Product) => void;
}

export function SearchBox({ locale, placeholder, query, results, t, onChange, onSelect }: Props) {
    const wrapRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const normalized = query.trim();
    const visibleResults = useMemo(() => results.slice(0, 6), [results]);

    useEffect(() => {
        if (!normalized) setOpen(false);
        setActiveIndex(0);
    }, [normalized]);

    useEffect(() => {
        const pointerDown = (event: PointerEvent) => {
            if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener("pointerdown", pointerDown);
        return () => document.removeEventListener("pointerdown", pointerDown);
    }, []);

    useEffect(() => {
        const keydown = (event: KeyboardEvent) => {
            if ((event.ctrlKey || event.metaKey) && !event.shiftKey && event.key.toLowerCase() === "k") {
                event.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
                setTimeout(() => {
                    inputRef.current?.focus();
                    if (query.trim()) setOpen(true);
                }, 180);
            }
        };
        document.addEventListener("keydown", keydown);
        return () => document.removeEventListener("keydown", keydown);
    }, [query]);

    const choose = (product: Product) => {
        onChange("");
        setOpen(false);
        onSelect(product);
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Escape") {
            setOpen(false);
            event.currentTarget.blur();
            return;
        }
        if (!visibleResults.length) return;
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            setOpen(true);
            setActiveIndex(index => (index + (event.key === "ArrowDown" ? 1 : -1) + visibleResults.length) % visibleResults.length);
            return;
        }
        if (event.key === "Enter" && open) {
            event.preventDefault();
            choose(visibleResults[activeIndex] ?? visibleResults[0]);
        }
    };

    return (
        <div className="search-wrap" ref={wrapRef}>
            <label className="search">
                <Icon name="search" />
                <input
                    ref={inputRef}
                    id="storefront-search-input"
                    value={query}
                    onChange={event => { onChange(event.currentTarget.value); setOpen(true); }}
                    onFocus={() => { if (normalized) setOpen(true); }}
                    onKeyDown={onKeyDown}
                    placeholder={placeholder}
                    autoComplete="off"
                    aria-autocomplete="list"
                    aria-controls="storefront-search-results"
                    aria-expanded={open && visibleResults.length > 0}
                />
                <span className="search-kbd">⌘ K</span>
            </label>
            <div id="storefront-search-results" className={`search-results${open && visibleResults.length ? " open" : ""}`} role="listbox">
                {visibleResults.map((product, index) => {
                    return (
                        <button
                            className={`search-result${index === activeIndex ? " active" : ""}`}
                            key={product.id}
                            type="button"
                            role="option"
                            aria-selected={index === activeIndex}
                            onPointerMove={() => setActiveIndex(index)}
                            onClick={() => choose(product)}
                        >
                            <span className="search-result__img"><img className={isPlaceholderImage(product.image) ? "product-photo--placeholder" : ""} src={product.image} alt="" /></span>
                            <span className="search-result__copy"><strong dir="auto">{product.name}</strong><small dir="auto">{product.description}</small></span>
                            {product.price !== null ? <span className="search-result__price" dir="ltr">{formatCurrency(product.price, locale)}</span> : null}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
